import React, { useState } from 'react';
import { X } from 'lucide-react';
import { ageBooks } from '../data/mock';

const NotifyMeModal = ({ isOpen, onClose, initialAge }) => {
  const [email, setEmail] = useState('');
  const [selectedAges, setSelectedAges] = useState(initialAge ? [initialAge] : []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Only show ages 11-12 for coming soon books
  const comingSoonBooks = ageBooks.filter(book => book.age >= 11 && book.age <= 12);

  const handleAgeToggle = (age) => {
    setSelectedAges(prev =>
      prev.includes(age)
        ? prev.filter(a => a !== age)
        : [...prev, age]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/notifications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          selected_ages: selectedAges
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to subscribe');
      }

      setIsSubmitting(false);
      setIsSuccess(true);
      
      // Reset and close after 2 seconds
      setTimeout(() => {
        setIsSuccess(false);
        setEmail('');
        setSelectedAges(initialAge ? [initialAge] : []);
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Subscription error:', error);
      setIsSubmitting(false);
      // Show error feedback
      alert('Failed to subscribe. Please try again.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0, 0, 0, 0.5)' }}>
      <div className="max-w-2xl w-full relative" style={{ 
        background: 'var(--glass-card)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        borderRadius: '1.5rem',
        padding: '2rem',
        border: '1px solid var(--glass-border)',
        boxShadow: 'var(--glass-shadow), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full transition-colors"
          style={{
            background: 'rgba(255, 255, 255, 0.6)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)'
          }}
          aria-label="Close modal"
        >
          <X size={20} />
        </button>

        {!isSuccess ? (
          <>
            <h2 className="heading-2 mb-4">Get Notified When These Books Launch</h2>
            <p className="body-medium mb-6" style={{ color: 'var(--text-secondary)' }}>
              Select the ages you'd like to be notified about when they become available for pre-order.
            </p>

            <form onSubmit={handleSubmit}>
              <div className="mb-6">
                <label className="body-small font-medium mb-2 block">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-border-input focus:border-border-input-focus outline-none transition-all"
                  style={{ 
                    background: 'rgba(255, 255, 255, 0.7)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)'
                  }}
                />
              </div>

              <div className="mb-6">
                <label className="body-small font-medium mb-3 block">
                  Select Ages to be Notified About *
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-64 overflow-y-auto p-2" style={{
                  background: 'rgba(255, 255, 255, 0.3)',
                  borderRadius: '0.75rem',
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                  {comingSoonBooks.map((book) => (
                    <label
                      key={book.age}
                      className="flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all hover:border-brand-primary"
                      style={{
                        borderColor: selectedAges.includes(book.age) ? 'var(--brand-primary)' : 'var(--border-input)',
                        background: selectedAges.includes(book.age) 
                          ? 'rgba(91, 127, 255, 0.15)' 
                          : 'rgba(255, 255, 255, 0.5)',
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedAges.includes(book.age)}
                        onChange={() => handleAgeToggle(book.age)}
                        className="w-4 h-4"
                      />
                      <span className="body-small font-medium">Age {book.age}</span>
                    </label>
                  ))}
                </div>
                {selectedAges.length === 0 && (
                  <p className="caption text-red-500 mt-2">Please select at least one age</p>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={isSubmitting || selectedAges.length === 0}
                  className="btn-primary flex-1"
                  style={{ opacity: (isSubmitting || selectedAges.length === 0) ? 0.5 : 1 }}
                >
                  {isSubmitting ? 'Submitting...' : 'Notify Me'}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="text-center py-8">
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ 
              background: 'linear-gradient(135deg, rgba(78, 205, 196, 0.2) 0%, rgba(122, 231, 199, 0.2) 100%)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: '2px solid var(--brand-success)'
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--brand-success)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
            <h3 className="heading-2 mb-2">You're All Set!</h3>
            <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
              We'll notify you when {selectedAges.length === 1 ? 'this book is' : 'these books are'} available for pre-order.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotifyMeModal;
